import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import * as React from 'react';
import { _className, ComponentSync, EditableDBItemV3, Grid, LL_H_C, LL_V_L } from '@nu-art/thunderstorm-frontend/index';
import { compare, sortArray } from '@nu-art/ts-common';
import { LikelihoodLabelMap, PermissionKey_DiseaseProfile_Edit, SeverityLabelMap, } from '@app/dp-shared';
import { ModuleFE_PermissionsAssert } from '@nu-art/permissions-frontend/modules/ModuleFE_PermissionsAssert';
import { DropDown_Values, ModuleFE_Reference, ModuleFE_Tag, ModuleFE_Value, ModuleFE_Variable, QRangeInput, QScrollWrapper, resolveReferenceDisplay, } from '@app/hcs-frontend';
import { resolveValueDisplay, valueType_Enumerated, valueType_Range, valueType_Tuple, } from '@app/hcs-shared';
import { PermissionsEditableComponent } from '@nu-art/permissions-frontend/index';
import { ICONS } from '@app/styles';
import { DropDown_Likelihood, DropDown_LikelihoodDirection, DropDown_Severity, } from '../../../_enum.js';
import { ModuleFE_DiseaseProfile, ModuleFE_DiseaseValue, ModuleFE_MultiVar } from '../../../_entity.js';
const displaySections = ['Values', 'References', 'Disease Info', 'Snomed Info'];
const dd_carets = {
    open: _jsx(ICONS.advisorArrowDown.component, {}),
    close: _jsx(ICONS.advisorArrowDown.component, { className: 'close' })
};
export class Renderer_DiseaseVariableData extends ComponentSync {
    // ######################## Static ########################
    dpPermissionKey_Edit = ModuleFE_PermissionsAssert.getPermissionKey(PermissionKey_DiseaseProfile_Edit);
    // ######################## Life Cycle ########################
    deriveStateFromProps(nextProps, state) {
        if (state.diseaseVariable?.item._id !== nextProps.diseaseVariableId)
            state.diseaseVariable = this.getEditableDiseaseVariable(ModuleFE_MultiVar.cache.unique(nextProps.diseaseVariableId));
        state.diseaseProfile = ModuleFE_DiseaseProfile.cache.unique(nextProps.diseaseProfileId);
        state.editMode = nextProps.editMode;
        state.displayedSection ??= displaySections[0];
        return state;
    }
    // ######################## Logic ########################
    getEditableDiseaseVariable(mv) {
        return new EditableDBItemV3(mv, ModuleFE_MultiVar)
            .setOnChanged(async (editable) => this.setState({ diseaseVariable: editable }));
    }
    getVariants = () => {
        const variantIds = this.state.diseaseVariable.item.variables?.[0].tagIds.find(group => group.key === this.state.diseaseProfile.variantsGroupId)?.value ?? [];
        return ModuleFE_Tag.cache.filter(i => variantIds.includes(i._id));
    };
    saveDV = async (dv) => {
        await dv.save();
        this.forceUpdate();
    };
    getLikelihoodString = (dv) => {
        let toRet = '';
        if (dv.item.likelihoodRange) {
            toRet += dv.item.likelihoodRange[0] === dv.item.likelihoodRange[1]
                ? dv.item.likelihoodRange[0]
                : `${dv.item.likelihoodRange[0]}-${dv.item.likelihoodRange[1]}`;
            toRet += '%';
        }
        if (dv.item.likelihood) {
            if (toRet !== '')
                toRet += ', ';
            toRet += LikelihoodLabelMap[dv.item.likelihood];
        }
        if (dv.item.likelihoodDirection) {
            if (toRet !== '')
                toRet += ', ';
            toRet += dv.item.likelihoodDirection;
        }
        return toRet;
    };
    onDiseaseLikelihoodRangeUpdated = async (diseaseValue, likelihood) => {
        //If no range in an empty DV
        if (!likelihood && !diseaseValue.item._id)
            return;
        //If range wasn't changed
        if (compare(likelihood, diseaseValue.item.likelihoodRange))
            return;
        if (likelihood)
            diseaseValue.set('likelihoodRange', likelihood);
        else
            delete diseaseValue.item.likelihoodRange;
        return this.saveDV(diseaseValue);
    };
    onDiseaseLikelihoodSelected = async (diseaseValue, likelihood) => {
        if (likelihood === diseaseValue.item.likelihood)
            return;
        diseaseValue.set('likelihood', likelihood);
        return this.saveDV(diseaseValue);
    };
    onDiseaseLikelihoodDirectionSelected = async (diseaseValue, likelihoodDirection) => {
        if (likelihoodDirection === diseaseValue.item.likelihoodDirection)
            return;
        diseaseValue.set('likelihoodDirection', likelihoodDirection);
        return this.saveDV(diseaseValue);
    };
    // ######################## Render - Structure ########################
    render() {
        return _jsxs(LL_V_L, { className: 'manager__disease-profile__disease-variable-data', children: [this.renderHeader(), this.renderBody()] });
    }
    renderHeader = () => {
        const variable = ModuleFE_Variable.cache.unique(this.state.diseaseVariable.item._firstVarId);
        const variantTags = this.getVariants();
        return _jsxs(LL_V_L, { className: 'manager__disease-profile__disease-variable-data__header', children: [_jsx("div", { className: 'manager__disease-profile__disease-variable-data__header__title', children: variable.name }), _jsx("div", { className: 'manager__disease-profile__disease-variable-data__header__sub-title', children: variantTags.map(i => i.label).join(' | ') })] });
    };
    renderBody = () => {
        return _jsxs(LL_H_C, { className: 'manager__disease-profile__disease-variable-data__body', children: [this.renderBodySectionSelector(), _jsxs(LL_V_L, { style: { height: '100%', flexGrow: 1, gap: '10px' }, children: [this.renderEditModeActions(), _jsx(QScrollWrapper, { children: _jsxs(Grid, { className: 'manager__data-grid manager__disease-profile__disease-variable-data__section-data', children: [this.renderValuesSection(), this.renderReferencesSection(), this.renderDiseaseInfoSection(), this.renderSnomedInfoSection()] }) })] })] });
    };
    renderBodySectionSelector = () => {
        return _jsx(LL_V_L, { className: 'manager__disease-profile__disease-variable-data__section-selector', children: displaySections.map((section, i) => {
                const className = _className('manager__disease-profile__disease-variable-data__section-selector__item', this.state.displayedSection === section && 'selected');
                return _jsx("div", { className: className, onClick: () => this.setState({ displayedSection: section }), children: section }, i);
            }) });
    };
    // ######################## Render - Sections ########################
    renderValuesSection = () => {
        if (this.state.displayedSection !== 'Values')
            return;
        const editMode = this.state.editMode;
        const mv = this.state.diseaseVariable;
        const diseaseValues = ModuleFE_DiseaseValue.cache.filter(i => i.multiVarId === mv.item._id);
        sortArray(diseaseValues, i => i.__created);
        return diseaseValues.map((_dv, i) => {
            const dv = new EditableDBItemV3(_dv, ModuleFE_DiseaseValue);
            return _jsxs(React.Fragment, { children: [mv.item.variables?.map(mvv => {
                        const variable = ModuleFE_Variable.cache.unique(mvv.variableId);
                        const valueId = dv.item.values?.[mvv.variableId];
                        const value = ModuleFE_Value.cache.unique(valueId);
                        if (!value && !editMode)
                            return;
                        if (editMode) {
                            let innerValueIds = [];
                            const containerValue = ModuleFE_Value.cache.unique(variable.valueId);
                            switch (containerValue.type) {
                                case valueType_Range:
                                    innerValueIds = containerValue.data.subsets;
                                    break;
                                case valueType_Enumerated:
                                    innerValueIds = containerValue.data.scope;
                                    break;
                                case valueType_Tuple:
                                    innerValueIds = containerValue.data.scope;
                                    break;
                            }
                            return _jsxs(React.Fragment, { children: [_jsx("div", { className: 'data-grid__label', children: variable._id === mv.item._firstVarId ? 'Value' : variable.name }), _jsx(DropDown_Values, { variable: variable, placeholder: 'Select', caret: dd_carets, selected: value, canUnselect: true, queryFilter: item => innerValueIds.includes(item._id), onSelected: () => {
                                        } })] }, variable._id);
                        }
                        return _jsxs(React.Fragment, { children: [_jsx("div", { className: 'data-grid__label', children: variable._id === mv.item._firstVarId ? 'Value' : variable.name }), _jsx("div", { className: 'data-grid__data', children: resolveValueDisplay(value) })] }, variable._id);
                    }), this.renderLikelihood(dv), this.renderSeverity(dv), i !== diseaseValues.length - 1 && _jsx("div", { className: 'grid-separator' })] }, dv.item._id);
        });
    };
    renderReferencesSection = () => {
        if (this.state.displayedSection !== 'References')
            return;
        const mv = this.state.diseaseVariable;
        return _jsx(_Fragment, { children: mv.item.referenceIds?.map(refId => {
                const ref = ModuleFE_Reference.cache.unique(refId);
                return _jsxs(_Fragment, { children: [_jsx("div", { className: 'data-grid__label', children: ref.type }), _jsx("div", { className: 'data-grid__data', children: resolveReferenceDisplay(ref) })] });
            }) });
    };
    renderDiseaseInfoSection = () => {
        if (this.state.displayedSection !== 'Disease Info')
            return;
        const variable = ModuleFE_Variable.cache.unique(this.state.diseaseVariable.item._firstVarId);
        const variantTags = this.getVariants();
        return _jsxs(_Fragment, { children: [_jsx("div", { className: 'data-grid__label', children: "Perspective" }), _jsx("div", { className: 'data-grid__data', children: variable.perspective ?? '-' }), _jsx("div", { className: 'data-grid__label', children: "Variants" }), variantTags.length
                    ? _jsx(LL_V_L, { children: variantTags.map(tag => _jsx("div", { className: 'data-grid__data', children: tag.label })) })
                    : _jsx("div", { className: 'data-grid__data', children: "-" })] });
    };
    renderSnomedInfoSection = () => {
        if (this.state.displayedSection !== 'Snomed Info')
            return;
        const variable = ModuleFE_Variable.cache.unique(this.state.diseaseVariable.item._firstVarId);
        const references = variable.referenceIds.map(i => ModuleFE_Reference.cache.unique(i));
        const snomedRef = references.find(i => i.type === 'snomed');
        const icdRef = references.find(i => i.type === 'icdCode');
        let icd10 = snomedRef?.data.icd10?.trim();
        if (icd10?.toLowerCase() !== 'not in scope.')
            icd10 = icd10?.replace(/\s+/g, ', ');
        return _jsxs(_Fragment, { children: [_jsx("div", { className: 'data-grid__label', children: "Snomed ID" }), _jsx("div", { className: 'data-grid__data', children: snomedRef?.data.id ?? '-' }), _jsx("div", { className: 'data-grid__label', children: "Snomed Name" }), _jsx("div", { className: 'data-grid__data', children: snomedRef?.data.names?.join(', ') ?? '-' }), _jsx("div", { className: 'data-grid__label', children: "Snomed ICD10" }), _jsx("div", { className: 'data-grid__data', children: icd10 ?? '-' }), _jsx("div", { className: 'data-grid__label', children: "ICD Code" }), _jsx("div", { className: 'data-grid__data', children: icdRef?.data.id ?? '-' })] });
    };
    // ######################## Render - Sections ########################
    renderEditModeActions = () => {
        if (this.state.displayedSection !== 'Values' || !this.state.editMode)
            return;
        return _jsxs(LL_H_C, { className: 'manager__disease-profile__disease-variable-data__edit-actions', children: [_jsx("div", { className: 'manager__disease-profile__disease-variable-data__edit-action', children: "+ Add Value" }), _jsx("div", { className: 'manager__disease-profile__disease-variable-data__edit-action', children: "+ Add Attribute" }), _jsx("div", { className: 'manager__disease-profile__disease-variable-data__edit-action', children: "+ Add Variable" })] });
    };
    // ######################## Render - Specific Parts ########################
    renderLikelihood = (dv) => {
        const likelihoodStr = this.getLikelihoodString(dv);
        const editMode = this.state.editMode;
        if (!likelihoodStr && !editMode)
            return;
        return _jsxs(React.Fragment, { children: [_jsx("div", { className: 'data-grid__label', children: "Likelihood" }), _jsx(PermissionsEditableComponent, { permissionKey: this.dpPermissionKey_Edit, value: likelihoodStr, forceLock: !editMode, className: 'data-grid__data', children: _jsxs(LL_V_L, { className: 'dv-likelihood', children: [_jsxs(LL_H_C, { className: 'dv-likelihood__range', children: [_jsx(QRangeInput, { value: dv.item.likelihoodRange, onValueBlur: val => this.onDiseaseLikelihoodRangeUpdated(dv, val) }), _jsx("span", { className: 'dv-likelihood__range__percent', children: "%" })] }), _jsx(DropDown_Likelihood.selectable, { selected: dv.item.likelihood, onSelected: likelihood => this.onDiseaseLikelihoodSelected(dv, likelihood), canUnselect: true }), _jsx(DropDown_LikelihoodDirection.selectable, { selected: dv.item.likelihoodDirection, canUnselect: true, onSelected: direction => this.onDiseaseLikelihoodDirectionSelected(dv, direction) })] }) })] }, `${dv.item._id}-likelihood`);
    };
    renderSeverity = (dv) => {
        const editMode = this.state.editMode;
        if (!dv.item.severityFactor && !editMode)
            return;
        if (editMode) {
            return _jsxs(React.Fragment, { children: [_jsx("div", { className: 'data-grid__label', children: "Severity" }), _jsx(DropDown_Severity.selectable, { selected: dv.item.severityFactor, canUnselect: true, onSelected: severity => {
                            dv.set('severityFactor', severity);
                            this.saveDV(dv);
                        } })] }, `${dv.item._id}-severity`);
        }
        return _jsxs(React.Fragment, { children: [_jsx("div", { className: 'data-grid__label', children: "Severity" }), _jsx("div", { className: 'data-grid__data', children: SeverityLabelMap[dv.item.severityFactor] })] }, `${dv.item._id}-severity`);
    };
}
//# sourceMappingURL=Renderer_DiseaseVariableData.js.map